import { Injectable } from '@angular/core';
import { Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";
import {FundingSourceStatus, FundingSourceType} from "@app/shared/enums/payment-type.enum";
import {OrgBeneficialOwner, OrgUnit} from "@app/shared/classes/org-unit";
import {PaymentTransactionMultipleHistory} from "@app/shared/classes/payment-transaction-multiple-history";
import {PaymentTxnType} from "@app/shared/enums/txn-type.enum";
import {QueryResult} from "@app/shared/classes/query-result";
import { OrgPaymentTransaction } from "@app/shared/ach/org-payment-transaction";
import {AchCustomer, AchCustomerSearchType, DwollaCustomer} from "@app/shared/classes/ach-customer";

@Injectable()
export class AchDataService {

  constructor (private http: HttpClient) {
  }

  private baseUrl = "/ach";

  retryVerification(orgUnit: OrgUnit): Observable<any> {
    let url = this.baseUrl + "/verified-customer/verify";
    return this.http.post<any>(url, orgUnit);
  }

  getRequiredDocuments(achCustomerDetails: AchCustomer): Observable<any> {
    let url = this.baseUrl + `/customer/${achCustomerDetails.id}/document?dwollaId=${achCustomerDetails.customerId}`;
    return this.http.get<any>(url);
  }

  getAchCustomerForResource(resourceId: number, customerType: AchCustomerSearchType): Observable<any> {
    let url = this.baseUrl + `/customer?resourceId=${resourceId}&customerType=${customerType}`;
    return this.http.get<any>(url);
  }

  createBeneficialOwner(owner: OrgBeneficialOwner): Observable<any> {
    let url = this.baseUrl + "/beneficial-owner";
    return this.http.post<any>(url, owner);
  }

  updateCustomerStatus(customerId: string): Observable<any> {
    let url = this.baseUrl + "/customer/status";
    return this.http.put<any>(url, customerId);
  }

  getCustomerFromDwolla(customerId: string): Observable<DwollaCustomer> {
    let url = this.baseUrl + `/customer/dwolla/${customerId}`;
    return this.http.get<DwollaCustomer>(url);
  }

  updateCustomerInDwolla(customerId: string, dwollaCustomer: DwollaCustomer): Observable<DwollaCustomer> {
    let url = this.baseUrl + `/customer/dwolla/${customerId}`;
    return this.http.put<DwollaCustomer>(url, dwollaCustomer);
  }

  getPaymentTransactionStatus(refId: number, paymentTxnType: PaymentTxnType): Observable<any> {
    let url = this.baseUrl + `/transaction?refId=${refId}&transaction=${paymentTxnType}`;
    return this.http.get(url);
  }

  updateTransactionStatus(transaction: OrgPaymentTransaction): Observable<any> {
    let url = this.baseUrl + `/transaction/${transaction.id}/status`;
    return this.http.get(url);
  }

  updateTransactionStatusByReference(transaction: OrgPaymentTransaction): Observable<any> {
    let url = this.baseUrl + `/transaction/status`;
    return this.http.put(url, transaction.transactionId);
  }

  getPaymentTransactionFailureReason(transaction: OrgPaymentTransaction): Observable<any> {
    let url = this.baseUrl + `/transaction/${transaction.id}/failure`;
    if (transaction && transaction.transactionId) {
      url = url + `?dwollaId=${transaction.transactionId}`;
    }
    return this.http.get(url);
  }

  getTransactionsForVendor(vendorId: number): Observable<any> {
    let url = this.baseUrl + `/transaction/summary?voucherRegister.invoicePayment.invoice.vendorId=${vendorId}`;
    return this.http.get(url);
  }

  getTransactionHistory(query: string): Observable<QueryResult> {
    let url = this.baseUrl + `/transaction/summary?${query}`;
    return this.http.get<QueryResult>(url);
  }

  getVoucherRegisterForTransaction(dwollaId: string, paymentTxnType: PaymentTxnType): Observable<PaymentTransactionMultipleHistory> {
    let url = this.baseUrl + `/transaction/multiple/status?transactionId=${dwollaId}&txnType=${paymentTxnType}`;
    return this.http.get<PaymentTransactionMultipleHistory>(url);
  }

  getFundingSourceForResource(resourceId: number, fundingSourceType: FundingSourceType, status: FundingSourceStatus): Observable<QueryResult> {
    let url = this.baseUrl + `/funding-source/search?resourceId=${resourceId}&fundingSourceType=${fundingSourceType}&status=${status}`;
    return this.http.get<QueryResult>(url);
  }

  retryPaymentForFailureTransaction(transaction: OrgPaymentTransaction): Observable<QueryResult> {
    let url = this.baseUrl + `/transaction/retry-payment?transactionId=${transaction.transactionId}`;
    return this.http.put<QueryResult>(url, null);
  }

  cancelPayment(transaction: OrgPaymentTransaction): Observable<QueryResult> {
    let url = this.baseUrl + `/transaction/cancel?transactionId=${transaction.transactionId}`;
    //TODO: Check if entire transaction needs to be sent in payload
    return this.http.put<QueryResult>(url, null);
  }

  retryPaymentForFailureTransactionById(transactionId: string): Observable<any> {
    let url = this.baseUrl + `/transaction/retry-payment?transactionId=${transactionId}`;
    return this.http.put(url, null);
  }

  getPayrollDisbursementSummary(payrollPeriodId: number): Observable<any> {
    let url = this.baseUrl + `/payroll-disbursement-summary?payrollPeriodId=${payrollPeriodId}`;
    return this.http.get(url);
  }

  getFeesForPayment(paymentType: PaymentTxnType, txnAmount?: number, noOfEmployees?: number): Observable<any> {
    let url = this.baseUrl + `/transaction/fees/calculate?paymentType=${paymentType}`;
    if (txnAmount) {
      url = url + `&paymentAmount=${txnAmount}`;
    }

    if (noOfEmployees) {
      url = url + `&noOfUsers=${noOfEmployees}`;
    }
    return this.http.get(url);
  }

  getBalanceForCustomer(orgId: number): Observable<any> {
    let url = this.baseUrl + `/verified-customer/balance?orgId=${orgId}`;
    return  this.http.get(url);
  }

  getIsValidRoutingNumber(routingNumbner: string): Observable<any> {
    let url = this.baseUrl + `/funding-source/validate-routing-number?routingNumber=${routingNumbner}`;
    return  this.http.get(url);
  }

}
